import {
  Activity,
  ArrowRight,
  BarChart3,
  Calculator,
  FileText,
  Gavel,
  GitCompareArrows,
  Kanban,
  LayoutDashboard,
  Search,
  Settings,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import { api } from '@/lib/api';
import type { Loan } from '@/types';
import { LOAN_STATUS_CONFIG } from '@/types';

interface SearchResult {
  id: string;
  type: 'loan' | 'borrower' | 'page';
  title: string;
  subtitle?: string;
  path: string;
  icon: typeof FileText;
  badge?: { label: string; color: string; bgColor: string };
}

const PAGES: SearchResult[] = [
  {
    id: 'p-dashboard',
    type: 'page',
    title: 'Dashboard',
    subtitle: 'Overview & analytics',
    path: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    id: 'p-loans',
    type: 'page',
    title: 'Loans',
    subtitle: 'Loan list',
    path: '/loans',
    icon: FileText,
  },
  {
    id: 'p-board',
    type: 'page',
    title: 'Loan Pipeline',
    subtitle: 'Kanban board',
    path: '/loans/board',
    icon: Kanban,
  },
  {
    id: 'p-borrowers',
    type: 'page',
    title: 'Borrowers',
    subtitle: 'Client directory',
    path: '/borrowers',
    icon: Users,
  },
  {
    id: 'p-reports',
    type: 'page',
    title: 'Reports',
    subtitle: 'Analytics & reporting',
    path: '/reports',
    icon: BarChart3,
  },
  {
    id: 'p-calculator',
    type: 'page',
    title: 'Calculator',
    subtitle: 'Loan calculator',
    path: '/calculator',
    icon: Calculator,
  },
  {
    id: 'p-collections',
    type: 'page',
    title: 'Collections',
    subtitle: 'Overdue management',
    path: '/collections',
    icon: Gavel,
  },
  {
    id: 'p-compare',
    type: 'page',
    title: 'Compare',
    subtitle: 'Compare loans side by side',
    path: '/loans/compare',
    icon: GitCompareArrows,
  },
  {
    id: 'p-activity',
    type: 'page',
    title: 'Activity Log',
    subtitle: 'Audit trail',
    path: '/activity',
    icon: Activity,
  },
  {
    id: 'p-import',
    type: 'page',
    title: 'Data Import',
    subtitle: 'Bulk CSV import',
    path: '/import',
    icon: Upload,
  },
  {
    id: 'p-settings',
    type: 'page',
    title: 'Settings',
    subtitle: 'Account settings',
    path: '/settings',
    icon: Settings,
  },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Listen for Cmd+K / Ctrl+K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Search logic
  const searchBackend = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      // Search loans and borrowers in parallel
      const [loanRes, borrowerRes] = await Promise.allSettled([
        api.get('/loans', { params: { limit: 8 } }),
        api.get('/borrowers'),
      ]);

      const searchResults: SearchResult[] = [];
      const lq = q.toLowerCase();

      // Filter loans client-side (backend doesn't have search param yet)
      if (loanRes.status === 'fulfilled') {
        const loans = (loanRes.value.data?.data ?? loanRes.value.data ?? []) as Loan[];
        const matched = loans
          .filter(
            l =>
              l.loanNumber?.toLowerCase().includes(lq) ||
              l.borrower?.name?.toLowerCase().includes(lq) ||
              l.loanType?.toLowerCase().includes(lq),
          )
          .slice(0, 5);

        for (const loan of matched) {
          const statusConfig = LOAN_STATUS_CONFIG[loan.status as keyof typeof LOAN_STATUS_CONFIG];
          searchResults.push({
            id: `loan-${loan.id}`,
            type: 'loan',
            title: `${loan.loanNumber} — ${loan.borrower?.name ?? 'Unknown'}`,
            subtitle: `$${parseFloat(loan.principal).toLocaleString('en-AU')} · ${loan.termMonths}mo · ${loan.loanType}`,
            path: `/loans/${loan.id}`,
            icon: FileText,
            badge: statusConfig
              ? {
                  label: statusConfig.label,
                  color: statusConfig.color,
                  bgColor: statusConfig.bgColor,
                }
              : undefined,
          });
        }
      }

      // Filter borrowers
      if (borrowerRes.status === 'fulfilled') {
        const borrowers = (borrowerRes.value.data ?? []) as Array<{
          id: string;
          name: string;
          email?: string;
          phone?: string;
        }>;
        const matched = borrowers
          .filter(b => b.name?.toLowerCase().includes(lq) || b.email?.toLowerCase().includes(lq))
          .slice(0, 5);

        for (const b of matched) {
          searchResults.push({
            id: `borrower-${b.id}`,
            type: 'borrower',
            title: b.name,
            subtitle: b.email || b.phone || undefined,
            path: `/borrowers/${b.id}`,
            icon: Users,
          });
        }
      }

      setResults(searchResults);
      setSelectedIndex(0);
    } catch {
      // Silently fail search
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search on query change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchBackend(query), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, searchBackend]);

  // Filter pages by query
  const filteredPages = useMemo(() => {
    if (!query.trim()) return PAGES;
    const q = query.toLowerCase();
    return PAGES.filter(
      p => p.title.toLowerCase().includes(q) || p.subtitle?.toLowerCase().includes(q),
    );
  }, [query]);

  // Combine: data results first, then pages
  const allResults = useMemo(() => {
    return [...results, ...filteredPages];
  }, [results, filteredPages]);

  function handleSelect(result: SearchResult) {
    navigate(result.path);
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (allResults[selectedIndex]) {
        handleSelect(allResults[selectedIndex]);
      }
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />

      {/* Palette */}
      <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search loans, borrowers, pages…"
            className="flex-1 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none bg-transparent"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <kbd className="text-[10px] font-mono text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {isSearching && <div className="px-4 py-3 text-xs text-gray-400">Searching…</div>}

          {/* Data results */}
          {results.length > 0 && (
            <div className="px-2 py-2">
              <p className="px-2 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                Results
              </p>
              {results.map((result, i) => {
                const isSelected = selectedIndex === i;
                return (
                  <Button
                    variant="secondary"
                    key={result.id}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(i)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-indigo-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <result.icon className="w-4 h-4 shrink-0 opacity-60" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{result.title}</p>
                      {result.subtitle && (
                        <p className="text-xs text-gray-400 truncate">{result.subtitle}</p>
                      )}
                    </div>
                    {result.badge && (
                      <span
                        className="text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0"
                        style={{ color: result.badge.color, backgroundColor: result.badge.bgColor }}
                      >
                        {result.badge.label}
                      </span>
                    )}
                    {isSelected && <ArrowRight className="w-3 h-3 shrink-0 opacity-40" />}
                  </Button>
                );
              })}
            </div>
          )}

          {/* Pages */}
          {filteredPages.length > 0 && (
            <div className="px-2 py-2 border-t border-gray-100">
              <p className="px-2 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                Pages
              </p>
              {filteredPages.map((page, i) => {
                const globalIdx = results.length + i;
                const isSelected = selectedIndex === globalIdx;
                return (
                  <Button
                    variant="secondary"
                    key={page.id}
                    onClick={() => handleSelect(page)}
                    onMouseEnter={() => setSelectedIndex(globalIdx)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-indigo-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <page.icon className="w-4 h-4 shrink-0 opacity-50" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{page.title}</p>
                      {page.subtitle && <p className="text-xs text-gray-400">{page.subtitle}</p>}
                    </div>
                    {isSelected && <ArrowRight className="w-3 h-3 shrink-0 opacity-40" />}
                  </Button>
                );
              })}
            </div>
          )}

          {!isSearching &&
            query.length >= 2 &&
            results.length === 0 &&
            filteredPages.length === 0 && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-gray-400">No results for "{query}"</p>
              </div>
            )}
        </div>

        {/* Footer hints */}
        <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-4 text-[10px] text-gray-400">
          <span className="flex items-center gap-1">
            <kbd className="border border-gray-200 rounded px-1 py-0.5 font-mono">↑↓</kbd> Navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="border border-gray-200 rounded px-1 py-0.5 font-mono">↵</kbd> Open
          </span>
          <span className="flex items-center gap-1">
            <kbd className="border border-gray-200 rounded px-1 py-0.5 font-mono">ESC</kbd> Close
          </span>
        </div>
      </div>
    </div>
  );
}
